
import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {Prop} from "vue-property-decorator";
import {EOrderStatuses, IOrder} from "@/model/Order/orderInterfaces";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {BoListPanel, BoPanel} from "bonomo/dist";

import LockerChip from "@/components/shared/chips/LockerChip.vue";
import LockerStatusChip from "@/components/shared/chips/LockerStatusChip.vue";
import OrderChip from "@/components/shared/chips/OrderChip.vue";
import UserChip from "@/components/shared/chips/UserChip.vue"
import ServiceProviderChip from "@/components/shared/chips/ServiceProviderChip.vue"
import Datim from "@/components/shared/Datim.vue";
import {IShortLocker, IShortUser} from '@/model/shortInterfaces';
import {UrlHelper} from "@/helpers/UrlHelper";
import {OrderHelper} from "@/helpers/OrderHelper";
import {IShortServiceProvider} from "@/model/shortInterfaces";
import {IUser} from "@/model/User/UserInterface";
import UserSearch from "@/components/user/widget/UserSearch.vue";
import UserNav from "@/components/user/widget/UserNav.vue";
import {IAuthUserData} from "@/store/modules/authModule";
import {IChipStyle} from "@/model/chipStyleInterfaces";
import CreateButton from "@/components/shared/CreateButton.vue";
const lockerModule = namespace("lockerModule");
const userModule = namespace("userModule");
const serviceProviderModule = namespace("serviceProviderModule")
const authModule = namespace("authModule");
const userPermissionModule = namespace("userPermissionModule");

type IOrderListItem = IOrder & {
  pickedUpAt?: string | null;
};

@Component({
  name: 'UserOrderList',
  components: {
    BoListPanel,
    BoPanel,
    Datim,
    LockerChip,
    LockerStatusChip,
    OrderChip,
    ServiceProviderChip,
    UserChip,
    UserSearch,
    UserNav,
    CreateButton,
  }
})

export default class UserOrderList extends Vue {
  @Prop()
  protected userId!: number;

  @userModule.Getter('user')
  protected user!: IUser;

  @userModule.Getter('orders')
  private orders!: IOrder[];

  @userModule.Getter('dashboard')
  protected dashboard!: any;

  @userModule.Action('fetchDashboard')
  private fetchDashboard!: (userId: number) => Promise<any>;

  @userModule.Action('fetchOrders')
  private fetchOrders!: (filters: any) => void;

  @userModule.Action('clearOrders')
  private clearOrders!: () => void;

  @lockerModule.Action('fetchLockers')
  private fetchLockers!: () => void;

  @lockerModule.Getter('lockers')
  private lockers!: IShortLocker[];

  @serviceProviderModule.Getter('serviceProviders')
  private providers!: IShortServiceProvider[];

  @serviceProviderModule.Action('fetchServiceProviders')
  private fetchServiceProviders!: () => void;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @userPermissionModule.Getter('permissionsUser')
  protected permissionsUser!: IAuthUserData;

  @userPermissionModule.Action('fetchPermissionsUser')
  protected fetchPermissionsUser!: (userId: number) => Promise<any>;

  protected get extraFilters(): object {
    return {
      userIds: [this.userId]
    }
  }

  get isSelf() {
    return '' + this.$route.params.userId === '' + this.authUser.id;
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id'), align: 'start', sortable: true, value: 'id' },
      { text: this.$t('common.type'), align: 'start',sortable: true, value: 'type'},
      { text: this.$t('common.name'), align: 'start', sortable: true, value: 'user' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: true, value: 'locker_id' },
      { text: this.$t('common.serviceProvider'), align: 'start', sortable: true, value: 'service_provider' },
      { text: this.$t('common.status'), align: 'start', sortable: true, value: 'status' },
      { text: this.$t("common.createdAt"), align: 'start', sortable: true, value: 'created_at' },
      { text: this.$t('orders.order.due'), align: 'start', sortable: true, value: 'due' },
      { text: this.$t('orders.order.grand_total'), align: 'start', sortable: true, value: 'grand_total' },
      { text: "", align: 'center', sortable: false, value: 'action' },
    ];
  }

  protected get oldPageUrl() {
    return '/mayor/users/' + this.userId + '/orders'
  }

  protected get createOrderTo() {
    return UrlHelper.mayorOrderCreate();
  }

  protected loadData(initial?: boolean){
    this.fetchPermissionsUser(this.userId as number);
    this.fetchDashboard(this.userId);
    if (initial) {
      this.fetchLockers();
      this.fetchServiceProviders();
    }
  }

  protected findLocker(id: number) {
    return this.lockers.find((locker :IShortLocker) => locker.id == id) || {};
  }

  protected linkToOrder(id: number) {
    return {name:'order',params: {orderId:id}};
  }

  protected orderChipStyle(type: string): IChipStyle {
    return OrderChip.chipStyleOf(type);
  }

  protected statusChipLabel(item: IOrderListItem): string {
    const base = '' + this.$t('orders.order.statuses.' + item.status);
    const ageHours = this.pendingPickupAgeHours(item);
    if (ageHours === null || ageHours < 24) {
      return base;
    }
    const ageSuffix = ageHours >= 72
      ? this.$t('orders.order.pendingPickupAgeTags.over72h')
      : this.$t('orders.order.pendingPickupAgeTags.over24h');
    return `${base} ${ageSuffix}`;
  }

  protected statusChipColor(item: IOrderListItem): string | undefined {
    const ageHours = this.pendingPickupAgeHours(item);
    if (ageHours === null || ageHours < 24) {
      return undefined;
    }
    return ageHours >= 72 ? 'red darken-2' : 'orange darken-2';
  }

  protected spikeErrorTitle(item: IOrderListItem): string {
    const err = item.spike_error;
    if (!err) return '';
    return this.$t('orders.order.logs.actions.700') + ': ' + err.operation + ' - ' + err.message;
  }

  private pendingPickupAgeHours(item: IOrderListItem): number | null {
    if (item.status !== EOrderStatuses.status_order_sp_delivered || item.pickedUpAt) {
      return null;
    }
    if (!item.created_at) {
      return null;
    }
    const createdAtMs = new Date(item.created_at).getTime();
    if (Number.isNaN(createdAtMs)) {
      return null;
    }
    return (Date.now() - createdAtMs) / (1000 * 60 * 60);
  }

  private typeChipTitle(order: IOrder): string {
    return order.type
        ? '' + (this.$t("orders.order.types." + (order.type || "office_order")) || this.$t("orders.order.types.unknown"))
        : '';
  }

  private gotoUser(user: IShortUser) {
    return this.$router.push({name: 'userOrderLists', params: { userId: ''+user.id}});
  }


}
